// ---------------------------------------------------------------------------
// Internal Auth Token Verification — constant-time comparison
// ---------------------------------------------------------------------------
// Security remediation 0.2 + 3.3: Shared utility for verifying X-Internal-Token
// on Durable Object entry points and Worker-to-Worker service binding calls.
//
// Two comparison functions:
//   constantTimeEqual     — XOR-based, for fixed-length values (hex signatures)
//   constantTimeSecretEqual — HMAC-normalized, for secrets of unknown/variable length
//
// verifyInternalToken uses constantTimeSecretEqual to eliminate length oracle.

const INTERNAL_TOKEN_HEADER = "X-Internal-Token";

type WebBufferSource = ArrayBufferView | ArrayBuffer;

type SubtleCryptoWithTimingSafeEqual = SubtleCrypto & {
  timingSafeEqual?: (left: WebBufferSource, right: WebBufferSource) => boolean;
};

/**
 * Constant-time string comparison (XOR-based).
 *
 * Suitable for fixed-length values where length is already known/public
 * (e.g., hex-encoded HMAC signatures, UUIDs).
 *
 * WARNING: Leaks string length via early return. Do NOT use for secret
 * comparison where length is sensitive. Use constantTimeSecretEqual instead.
 */
export function constantTimeEqual(a: string, b: string): boolean {
  if (a.length !== b.length) return false;
  let result = 0;
  for (let i = 0; i < a.length; i++) {
    result |= a.charCodeAt(i) ^ b.charCodeAt(i);
  }
  return result === 0;
}

// Eagerly-initialized CryptoKey for HMAC normalization.
// const Promise — no mutable module-level state (anti-pattern #3).
// The key value is deterministic — only used to normalize inputs to
// fixed-length digests before comparison. Same across all requests/tenants.
const hmacKeyPromise: Promise<CryptoKey> = crypto.subtle.importKey(
  "raw",
  new TextEncoder().encode("ease-internal-secret-comparison"),
  { name: "HMAC", hash: "SHA-256" },
  false,
  ["sign"],
);

/**
 * Constant-time secret comparison using HMAC normalization.
 *
 * HMACs both inputs to fixed-length digests, then uses the native
 * crypto.subtle.timingSafeEqual for comparison. This eliminates:
 *   - Length oracle (HMAC output is always 32 bytes regardless of input)
 *   - JS engine timing variance (native comparison, not JS loop)
 *
 * Use this for all secret/token comparisons. Use constantTimeEqual only
 * for fixed-length public-format values (hex signatures).
 */
export async function constantTimeSecretEqual(
  a: string,
  b: string,
): Promise<boolean> {
  if (!a || !b) return false;

  const encoder = new TextEncoder();
  const key = await hmacKeyPromise;

  const [digestA, digestB] = await Promise.all([
    crypto.subtle.sign("HMAC", key, encoder.encode(a)),
    crypto.subtle.sign("HMAC", key, encoder.encode(b)),
  ]);
  const subtleCrypto = crypto.subtle as SubtleCryptoWithTimingSafeEqual;

  // crypto.subtle.timingSafeEqual is a Cloudflare Workers extension.
  // Fallback to XOR comparison on equal-length HMAC digests for Node/test envs.
  if (typeof subtleCrypto.timingSafeEqual === "function") {
    return subtleCrypto.timingSafeEqual(digestA, digestB);
  }

  // Fallback: XOR on HMAC digests (always same length = 32 bytes)
  const viewA = new Uint8Array(digestA);
  const viewB = new Uint8Array(digestB);
  let result = 0;
  for (let i = 0; i < viewA.length; i++) {
    result |= (viewA[i] ?? 0) ^ (viewB[i] ?? 0);
  }
  return result === 0;
}

/**
 * Verify X-Internal-Token header on an incoming request.
 * Returns true if the token matches the expected value.
 * Uses HMAC-normalized constant-time comparison (no length oracle).
 * Returns false if the header is missing, empty, or doesn't match.
 */
export async function verifyInternalToken(
  request: Request,
  expectedToken: string,
): Promise<boolean> {
  const provided = request.headers.get(INTERNAL_TOKEN_HEADER);
  if (!provided || !expectedToken) return false;
  return constantTimeSecretEqual(provided, expectedToken);
}

/**
 * Create a 401 Unauthorized response for failed internal auth.
 * Generic error — no information about why auth failed.
 */
export function unauthorizedResponse(): Response {
  return Response.json({ error: "unauthorized" }, { status: 401 });
}
